import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  deleteConfig,
  fetchFeatureFields,
  generateConfigMeta,
  getConfig,
  listConfigs,
  saveConfig,
  type FeatureField,
  type TpeFeatureConfig,
} from "../api/client";
import { getCurrentUserEmail } from "../auth/cognito";

const TPE_ADMIN_EMAILS = new Set(["nick@kitmitto.com"]);

type OverrideState = Record<string, unknown>;

/** Render one form field. The user can either leave it to Optuna (default)
 *  or pin to a specific value. For workflow-level fields (no search_range)
 *  the only choice is "use default" vs "pin". */
function FieldRow({
  field,
  value,
  pinned,
  onChange,
}: {
  field: FeatureField;
  value: unknown;
  pinned: boolean;
  onChange: (next: { pinned: boolean; value: unknown }) => void;
}) {
  const [showGuidance, setShowGuidance] = useState(false);
  const hasSearch = !!field.search_range;
  const showInput = pinned;

  const display = useMemo(() => {
    if (Array.isArray(field.search_range)) {
      const r = field.search_range as unknown[];
      if (field.categorical) return `categorical: ${r.map((x) => JSON.stringify(x)).join(", ")}`;
      if (r.length === 2) {
        const lo = JSON.stringify(r[0]);
        const hi = JSON.stringify(r[1]);
        return `${lo} → ${hi}${field.log_scale ? " (log)" : ""}`;
      }
      return JSON.stringify(r);
    }
    return null;
  }, [field]);

  function renderInput() {
    if (field.type === "bool") {
      const v = value === undefined || value === null ? !!field.default : !!value;
      return (
        <select value={String(v)} onChange={(e) => onChange({ pinned: true, value: e.target.value === "true" })}
                style={inputStyle}>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }
    if (field.categorical && Array.isArray(field.search_range)) {
      return (
        <select
          value={JSON.stringify(value ?? field.default)}
          onChange={(e) => onChange({ pinned: true, value: JSON.parse(e.target.value) })}
          style={inputStyle}
        >
          {(field.search_range as unknown[]).map((c, i) => (
            <option key={i} value={JSON.stringify(c)}>{JSON.stringify(c)}</option>
          ))}
        </select>
      );
    }
    if (field.type === "int" || field.type === "float") {
      return (
        <NumberInput
          initial={value ?? field.default}
          isInt={field.type === "int"}
          onChange={(v) => onChange({ pinned: true, value: v })}
        />
      );
    }
    return (
      <input type="text" value={String(value ?? field.default ?? "")}
             onChange={(e) => onChange({ pinned: true, value: e.target.value })}
             style={inputStyle} />
    );
  }

  return (
    <div style={{
      padding: "10px 0", borderBottom: "1px solid #1e293b",
      display: "flex", gap: 12, alignItems: "flex-start",
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <strong style={{ fontSize: 13 }}>{field.label}</strong>
          <code style={{ color: "#64748b", fontSize: 10 }}>{field.key}</code>
          {field.guidance && (
            <button
              type="button"
              onClick={() => setShowGuidance((s) => !s)}
              title="Show guidance"
              style={{
                background: "none", border: "none", color: "#93c5fd",
                cursor: "pointer", fontSize: 12, padding: 0,
              }}
            >ⓘ</button>
          )}
        </div>
        <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 2 }}>{field.description}</div>
        {display && (
          <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>
            Search space: <code>{display}</code> · default: <code>{JSON.stringify(field.default)}</code>
          </div>
        )}
        {showGuidance && field.guidance && (
          <div style={{
            background: "#0b1220", border: "1px solid #1e293b",
            padding: "8px 10px", borderRadius: 4, marginTop: 6,
            fontSize: 12, color: "#cbd5e1", lineHeight: 1.5,
          }}>
            {field.guidance}
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        {hasSearch ? (
          <select
            value={pinned ? "pin" : "search"}
            onChange={(e) => {
              if (e.target.value === "search") onChange({ pinned: false, value: undefined });
              else onChange({ pinned: true, value: value ?? field.default });
            }}
            style={{
              ...inputStyle, width: 130, fontSize: 11,
              background: pinned ? "#1e1b4b" : "#0b1220",
            }}
          >
            <option value="search">Optuna search</option>
            <option value="pin">Pin to value</option>
          </select>
        ) : (
          <select
            value={pinned ? "pin" : "default"}
            onChange={(e) => {
              if (e.target.value === "default") onChange({ pinned: false, value: undefined });
              else onChange({ pinned: true, value: value ?? field.default });
            }}
            style={{
              ...inputStyle, width: 130, fontSize: 11,
              background: pinned ? "#1e1b4b" : "#0b1220",
            }}
          >
            <option value="default">Use default</option>
            <option value="pin">Override</option>
          </select>
        )}
        {showInput && <div style={{ minWidth: 180 }}>{renderInput()}</div>}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: "#0f172a", color: "#e2e8f0",
  border: "1px solid #334155", borderRadius: 4,
  padding: "5px 8px", fontSize: 12, width: 180,
};

/** Numeric input that keeps the user's raw text in local state.
 *
 *  Why: if we stored only the parsed number and re-rendered the input
 *  from it, typing "0." would round-trip through parseFloat → 0 → "0",
 *  silently eating the decimal point so the user can never enter
 *  small fractional values like 0.0005. The raw text persists here;
 *  the parsed numeric value is pushed up so the override stays valid. */
function NumberInput({
  initial,
  isInt,
  onChange,
}: {
  initial: unknown;
  isInt: boolean;
  onChange: (v: number | string) => void;
}) {
  const [raw, setRaw] = useState<string>(
    initial === undefined || initial === null ? "" : String(initial),
  );
  return (
    <input
      type="text"
      inputMode={isInt ? "numeric" : "decimal"}
      value={raw}
      onChange={(e) => {
        const next = e.target.value;
        setRaw(next);
        if (next === "" || next === "-" || next === ".") {
          onChange(next);
          return;
        }
        const parsed = isInt ? parseInt(next, 10) : parseFloat(next);
        onChange(Number.isNaN(parsed) ? next : parsed);
      }}
      style={inputStyle}
    />
  );
}

function Editor({
  initial,
  fields,
  onSave,
  onCancel,
  isSaving,
}: {
  initial: TpeFeatureConfig | null;
  fields: FeatureField[];
  onSave: (c: { config_name: string; description: string; overrides: OverrideState }) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState(initial?.config_name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [overrides, setOverrides] = useState<OverrideState>(initial?.overrides ?? {});
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const pinCount = Object.keys(overrides).length;
  // Always allowed when there are pins. For existing configs the name is
  // the DDB primary key (immutable) — we only fill description in that case.
  const canGenerate = pinCount > 0 && !generating;

  async function autoGenerate() {
    setGenerating(true);
    setGenError(null);
    try {
      const r = await generateConfigMeta(overrides);
      if (!initial) setName(r.config_name);
      setDescription(r.description);
    } catch (e) {
      setGenError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
    }
  }

  const sections = useMemo(() => {
    const grouped: Record<string, FeatureField[]> = {};
    for (const f of fields) {
      (grouped[f.section] ||= []).push(f);
    }
    return Object.entries(grouped);
  }, [fields]);

  function update(key: string, next: { pinned: boolean; value: unknown }) {
    setOverrides((prev) => {
      const copy = { ...prev };
      if (next.pinned) copy[key] = next.value;
      else delete copy[key];
      return copy;
    });
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{initial ? `Edit: ${initial.config_name}` : "New config"}</h3>
      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ flex: "1 1 240px" }}>
          <div style={{ color: "#94a3b8", fontSize: 11, marginBottom: 2 }}>Name</div>
          <input
            type="text"
            value={name}
            disabled={!!initial}
            onChange={(e) => setName(e.target.value)}
            placeholder="alphanumeric, _, -, ."
            style={{ ...inputStyle, width: "100%", maxWidth: 360 }}
          />
        </label>
        <label style={{ flex: "2 1 360px" }}>
          <div style={{ color: "#94a3b8", fontSize: 11, marginBottom: 2 }}>Description (optional)</div>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="what is this config for?"
            style={{ ...inputStyle, width: "100%" }}
          />
        </label>
        <button
          type="button"
          onClick={autoGenerate}
          disabled={!canGenerate}
          title={
            pinCount === 0
              ? "Pin at least one setting first"
              : initial
              ? "Regenerate description from the pinned settings (name is locked)"
              : "Generate a name + description from the pinned settings (Bedrock)"
          }
          style={{
            background: canGenerate ? "#7c3aed" : "#334155",
            color: "#fff", border: "1px solid #6d28d9",
            padding: "8px 14px", borderRadius: 4, fontSize: 12, fontWeight: 600,
            cursor: canGenerate ? "pointer" : "not-allowed", whiteSpace: "nowrap",
          }}
        >
          {generating ? "Generating…" : "✨ Auto-generate"}
        </button>
      </div>
      {genError && (
        <div style={{
          background: "#3f1d1d", border: "1px solid #7f1d1d", color: "#fecaca",
          padding: "6px 10px", borderRadius: 4, fontSize: 12, marginBottom: 12,
        }}>
          Auto-generate failed: {genError}
        </div>
      )}

      {sections.map(([section, sectionFields]) => (
        <div key={section} style={{ marginBottom: 16 }}>
          <h4 style={{ marginBottom: 4, color: "#93c5fd" }}>{section}</h4>
          {sectionFields.map((f) => (
            <FieldRow
              key={f.key}
              field={f}
              value={overrides[f.key]}
              pinned={Object.prototype.hasOwnProperty.call(overrides, f.key)}
              onChange={(n) => update(f.key, n)}
            />
          ))}
        </div>
      ))}

      <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center" }}>
        <button
          onClick={() => onSave({ config_name: name, description, overrides })}
          disabled={isSaving || !name}
          style={{
            background: isSaving || !name ? "#334155" : "#16a34a",
            color: "#fff", border: "1px solid #15803d",
            padding: "8px 16px", borderRadius: 4, fontSize: 13, fontWeight: 600,
            cursor: isSaving || !name ? "not-allowed" : "pointer",
          }}
        >
          {isSaving ? "Saving…" : "Save config"}
        </button>
        <button
          onClick={onCancel}
          style={{
            background: "#1e293b", color: "#cbd5e1", border: "1px solid #334155",
            padding: "8px 16px", borderRadius: 4, fontSize: 13, cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <span style={{ color: "#64748b", fontSize: 11 }}>
          {pinCount} pin{pinCount === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  );
}

export function ConfigsPage() {
  const [email, setEmail] = useState<string | null | undefined>(undefined);
  const [editing, setEditing] = useState<TpeFeatureConfig | null | "new">(null);
  const qc = useQueryClient();

  useEffect(() => { getCurrentUserEmail().then(setEmail); }, []);
  const isAdmin = typeof email === "string" && TPE_ADMIN_EMAILS.has(email.toLowerCase());

  const fieldsQ = useQuery({ queryKey: ["feature-fields"], queryFn: fetchFeatureFields, enabled: isAdmin });
  const configsQ = useQuery({ queryKey: ["configs"], queryFn: listConfigs, enabled: isAdmin });

  const save = useMutation({
    mutationFn: saveConfig,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["configs"] });
      setEditing(null);
    },
    onError: (e: Error) => window.alert(`Save failed: ${e.message}`),
  });
  const del = useMutation({
    mutationFn: deleteConfig,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["configs"] }),
    onError: (e: Error) => window.alert(`Delete failed: ${e.message}`),
  });

  if (email === undefined) return <div>Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="card" style={{ color: "#fca5a5" }}>
        Not authorized.
        <div style={{ marginTop: 8 }}><Link to="/" className="backlink">← Leaderboard</Link></div>
      </div>
    );
  }

  if (editing === "new" && fieldsQ.data) {
    return (
      <div>
        <Link to="/admin/configs" className="backlink" onClick={(e) => { e.preventDefault(); setEditing(null); }}>
          ← Configs
        </Link>
        <Editor
          initial={null}
          fields={fieldsQ.data.fields}
          isSaving={save.isPending}
          onSave={(c) => save.mutate(c)}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }
  if (editing && editing !== "new" && fieldsQ.data) {
    return (
      <div>
        <Link to="/admin/configs" className="backlink" onClick={(e) => { e.preventDefault(); setEditing(null); }}>
          ← Configs
        </Link>
        <Editor
          initial={editing}
          fields={fieldsQ.data.fields}
          isSaving={save.isPending}
          onSave={(c) => save.mutate(c)}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <Link to="/admin/tpe" className="backlink">← TPE control</Link>
      <h2 style={{ marginTop: 12 }}>TPE configs</h2>
      <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 0 }}>
        Named bundles of feature flag / hyperparameter overrides. Pick a config when
        starting a TPE run to pin specific settings. Other settings continue to be
        searched by Optuna.
      </p>

      <div className="card" style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <button
          onClick={() => setEditing("new")}
          disabled={!fieldsQ.data}
          style={{
            background: fieldsQ.data ? "#16a34a" : "#334155",
            color: "#fff", border: "1px solid #15803d",
            padding: "8px 16px", borderRadius: 4, fontSize: 13, fontWeight: 600,
            cursor: fieldsQ.data ? "pointer" : "not-allowed",
          }}
        >
          + New config
        </button>
        <span style={{ color: "#94a3b8", fontSize: 12 }}>
          {configsQ.isLoading ? "loading…" : `${configsQ.data?.configs.length ?? 0} saved`}
        </span>
      </div>

      {(configsQ.error || fieldsQ.error) && (
        <div className="card" style={{ color: "#fca5a5" }}>
          Error: {String(configsQ.error || fieldsQ.error)}
        </div>
      )}

      <div className="card" style={{ overflow: "hidden" }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Pins</th>
                <th>Description</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(configsQ.data?.configs ?? []).map((c) => {
                const pinCount = Object.keys(c.overrides || {}).length;
                return (
                  <tr key={c.config_name}>
                    <td><code style={{ fontSize: 12 }}>{c.config_name}</code></td>
                    <td><code style={{ fontSize: 11, color: "#94a3b8" }}>{pinCount}</code></td>
                    <td style={{ fontSize: 12 }}>{c.description || <span style={{ color: "#64748b" }}>—</span>}</td>
                    <td style={{ fontSize: 11, color: "#94a3b8" }}>
                      {c.updated_at ? new Date(c.updated_at).toLocaleString() : "—"}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        onClick={async () => {
                          const full = await getConfig(c.config_name);
                          setEditing(full);
                        }}
                        style={ghostBtn}
                      >Edit</button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete config "${c.config_name}"?`)) {
                            del.mutate(c.config_name);
                          }
                        }}
                        style={{ ...ghostBtn, color: "#fca5a5", borderColor: "#7f1d1d" }}
                      >Delete</button>
                    </td>
                  </tr>
                );
              })}
              {configsQ.data?.configs.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", color: "#94a3b8", fontStyle: "italic", padding: 20 }}>
                  No configs yet. Click "+ New config" to create one.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const ghostBtn: React.CSSProperties = {
  background: "#1e293b", color: "#cbd5e1", border: "1px solid #334155",
  padding: "4px 10px", borderRadius: 3, fontSize: 12, cursor: "pointer",
  marginRight: 4,
};
